// 2026-05-30 / JM — Authoritative inventory of every QuickBooks transaction
// Uniformax creates, with the GL impact of each line. Hand-curated from
// server/UniformaxServer/Services/QuickbooksFactory.cs — keep in sync when
// new posting code lands.
//
// Why this file exists: which QBAccount/QBItem role each transaction uses
// was previously only visible by reading C#. Admins editing the role→QB
// mapping in QBO Account Setup couldn't see the consequences. This module
// is the data the screen renders to make the fan-out visible.
//
// Conventions
//   - 'DR' / 'CR' = the side of the journal entry this row represents.
//   - kind='account'  → references a QBAccount role (Cash / Sales / Returns)
//                       used for deposit-to / direct GL refs.
//   - kind='item'     → references a QBItem role (Product / Shipping /
//                       Handling / Tax / Returns); QB resolves the item to
//                       its associated income/expense GL account.
//   - kind='qbAuto'   → Uniformax doesn't set this side; QB infers it from
//                       the document type + customer (e.g. Accounts Receivable
//                       on an Invoice). Shown for journal-entry completeness.
//   - condition       → human-readable note when the line only appears under
//                       certain conditions (e.g. freight only if customer
//                       pays freight). null if the line is always present.

export type PostingSide = 'DR' | 'CR';
export type PostingKind = 'account' | 'item' | 'qbAuto';

export interface PostingEntry {
  side: PostingSide;
  kind: PostingKind;
  /** Role name for account/item kinds — matches QBAccount.* or QBItem.* in C#. Null for qbAuto. */
  role: string | null;
  /** Short human label for this row (e.g. "Products line", "Cash deposit"). */
  label: string;
  /** When the line only appears under a condition; null if always present. */
  condition?: string;
}

export interface PostingTransaction {
  /** Stable id for templates/keys. */
  id: string;
  /** What admins see as the heading on the card. */
  displayName: string;
  /** Plain-English description of when this transaction fires. */
  purpose: string;
  /** C# method in QuickbooksFactory that builds the QB object — for traceability. */
  factoryMethod: string;
  /** Where the transaction originates (which app flow triggers it). */
  trigger: string;
  /** Ordered debit/credit rows. */
  entries: PostingEntry[];
  /** Optional caveats to surface in the UI (e.g. "Tax is pre-calculated upstream"). */
  notes?: string[];
}

export interface PostingGroup {
  id: string;
  /** Section heading admins see. */
  displayName: string;
  /** One-line description of the flow this group represents. */
  blurb: string;
  transactions: PostingTransaction[];
}

// ============================================================
// Inventory — grouped by transaction flow (Scheme E)
// ============================================================
export const GL_POSTING_MAP: PostingGroup[] = [
  {
    id: 'sales',
    displayName: 'Sales',
    blurb: 'Documents Uniformax sends to QuickBooks when a customer is billed for goods or services.',
    transactions: [
      {
        id: 'invoice',
        displayName: 'Invoice — Net-Terms Shipped Order',
        purpose:
          'Customer invoice on a normal shipped order. Lists products, freight (if customer pays it), handling, and tax as separate line items.',
        factoryMethod: 'CreateQBInvoiceLocally',
        trigger: 'End-of-day "Post QB" for an invoice on a JO that is not p-card-on-ship.',
        entries: [
          { side: 'DR', kind: 'qbAuto', role: null, label: 'Accounts Receivable (QB automatic on Invoice)' },
          { side: 'CR', kind: 'item', role: 'Product', label: 'Products line' },
          {
            side: 'CR',
            kind: 'item',
            role: 'Shipping',
            label: 'Freight line',
            condition: 'Only if InvoiceHeader.shipFreightAmt > 0 AND JoHeader.doesAccountPayFreight = true',
          },
          { side: 'CR', kind: 'item', role: 'Handling', label: 'Handling line', condition: 'Only if InvoiceHeader.orderHandlingAmt > 0' },
          { side: 'CR', kind: 'item', role: 'Tax', label: 'Tax line', condition: 'Only if InvoiceHeader.taxAmt > 0' },
        ],
        notes: [
          "Tax is sent as a regular line item (not via QB's automated sales-tax feature). Tax is pre-calculated upstream by Uniformax.",
        ],
      },
      {
        id: 'invoice-account-owned',
        displayName: 'Invoice — Account-Owned Inventory Pull',
        purpose:
          "Customer invoice when the order pulled items from the account's own managed inventory. No freight (the account already paid to put the goods in stock).",
        factoryMethod: 'CreateQBInvoiceForAccountOwned',
        trigger: 'End-of-day "Post QB" for an account-owned invoice (JoHeader.isAccountOwnedOrder = true).',
        entries: [
          { side: 'DR', kind: 'qbAuto', role: null, label: 'Accounts Receivable (QB automatic on Invoice)' },
          { side: 'CR', kind: 'item', role: 'Product', label: 'Products line' },
          { side: 'CR', kind: 'item', role: 'Handling', label: 'Handling line', condition: 'Only if InvoiceHeader.orderHandlingAmt > 0' },
          { side: 'CR', kind: 'item', role: 'Tax', label: 'Tax line', condition: 'Only if InvoiceHeader.taxAmt > 0' },
        ],
      },
      {
        id: 'invoice-restock',
        displayName: 'Invoice — Restock Fee Batch',
        purpose:
          'Bundled restock-fee invoice. Restock fees from multiple returns on this account are batched into one invoice, one line per RRD.',
        factoryMethod: 'CreateQBInvoiceForRestock',
        trigger: 'Admin clicks "Invoice Restock Fees" on the Accounts page.',
        entries: [
          { side: 'DR', kind: 'qbAuto', role: null, label: 'Accounts Receivable (QB automatic on Invoice)' },
          {
            side: 'CR',
            kind: 'item',
            role: 'Returns',
            label: 'Restock-fee line',
            condition: 'One per RRD where ChargeRestockFee = true AND RestockFeeInvoiced = false',
          },
        ],
        notes: [
          'The Returns item is shared across this restock-fee invoice AND the refund-side documents (CreditMemo, RefundReceipt). Sharing means restock-fee revenue and refund deductions land in the same GL account today.',
        ],
      },
    ],
  },
  {
    id: 'receipts',
    displayName: 'Receipts (Money In)',
    blurb: 'Documents Uniformax sends to QuickBooks when money comes in from a customer.',
    transactions: [
      {
        id: 'sales-receipt-prepayment',
        displayName: 'Sales Receipt — Prepayment at Order Time',
        purpose:
          'Records a personal-card prepayment a customer made at order placement (e.g. over-allowance overage). Auth.net already moved the money; this is the QB journal entry.',
        factoryMethod: 'CreateQBSalesReceiptLocally',
        trigger:
          'Hangfire PostUnpostedSalesReceiptsToQB job, after a prepayment charge succeeded and JoHeader.prepaymentTransactionId was set.',
        entries: [
          { side: 'DR', kind: 'account', role: 'Cash', label: 'Cash deposit (DepositToAccountRef)' },
          { side: 'CR', kind: 'item', role: 'Product', label: 'Prepaid products line' },
        ],
      },
      {
        id: 'payment-pcard',
        displayName: 'Payment — P-Card Charge Applied to Invoice',
        purpose:
          'Records that an existing QB invoice was paid by the corporate p-card at ship time. Linked to the invoice (so it clears AR for that invoice).',
        factoryMethod: 'CreateQBPaymentLocally',
        trigger: 'End-of-day "Post QB" right after a p-card charge succeeds AND the invoice has already been posted to QB.',
        entries: [
          { side: 'DR', kind: 'account', role: 'Cash', label: 'Cash deposit (DepositToAccountRef)' },
          { side: 'CR', kind: 'qbAuto', role: null, label: 'Accounts Receivable — clears the invoice (QB automatic via LinkedTxn)' },
        ],
        notes: ['If the p-card amount exceeds the invoice total, the overage lands on UnappliedAmt — QB carries it as a customer credit.'],
      },
    ],
  },
  {
    id: 'refunds-and-credits',
    displayName: 'Refunds & Credits (Money Out / Adjustments)',
    blurb: 'Documents Uniformax sends to QuickBooks when a return resolves with a refund or a credit memo.',
    transactions: [
      {
        id: 'credit-memo',
        displayName: 'Credit Memo — Net-Terms Customer',
        purpose:
          "Issues a QB credit memo against the customer's account. Used when the original order was/will be invoiced on net terms (no Auth.net refund needed; the credit applies against future AR).",
        factoryMethod: 'CreateQBCreditMemoLocally',
        trigger: 'Hangfire PostUnpostedCreditMemosToQB job for a CreditMemo with type = Memo (not PrepaymentRefund / PcardRefund).',
        entries: [
          { side: 'DR', kind: 'item', role: 'Returns', label: 'Product Return line' },
          { side: 'CR', kind: 'qbAuto', role: null, label: 'Accounts Receivable (QB automatic on Credit Memo)' },
        ],
      },
      {
        id: 'refund-receipt',
        displayName: 'Refund Receipt — Auth.net Refund of Prepayment or P-Card',
        purpose:
          "Records the QB journal entry for an actual cash refund. Auth.net has already moved the money back to the customer's card; this captures the GL impact.",
        factoryMethod: 'CreateQBRefundReceiptLocally',
        trigger:
          'Hangfire PostUnpostedCreditMemosToQB job for a CreditMemo with type = PrepaymentRefund or PcardRefund, after the Auth.net refund succeeds.',
        entries: [
          { side: 'DR', kind: 'item', role: 'Returns', label: 'Product Return line' },
          { side: 'CR', kind: 'account', role: 'Cash', label: 'Cash deposit (DepositToAccountRef) — money came OUT of this account' },
        ],
        notes: [
          'On a Refund Receipt, the DepositToAccountRef is the account the money was drawn FROM, not deposited to. Same role as Sales Receipt / Payment but the side is inverted.',
        ],
      },
    ],
  },
];

// ============================================================
// Lookup helpers — for the UI to cross-reference a role to its currently
// configured QB Account or Item.
// ============================================================

/** Every account role referenced anywhere in the map (deduped). */
export function getReferencedAccountRoles(): string[] {
  const set = new Set<string>();
  for (const g of GL_POSTING_MAP) {
    for (const t of g.transactions) {
      for (const e of t.entries) {
        if (e.kind === 'account' && e.role) set.add(e.role);
      }
    }
  }
  return Array.from(set).sort();
}

/** Every item role referenced anywhere in the map (deduped). */
export function getReferencedItemRoles(): string[] {
  const set = new Set<string>();
  for (const g of GL_POSTING_MAP) {
    for (const t of g.transactions) {
      for (const e of t.entries) {
        if (e.kind === 'item' && e.role) set.add(e.role);
      }
    }
  }
  return Array.from(set).sort();
}

/** Every transaction id that references a given account role — used for the
 *  "this account is used by N transactions" cross-reference. */
export function getTransactionsUsingAccount(role: string): PostingTransaction[] {
  const hits: PostingTransaction[] = [];
  for (const g of GL_POSTING_MAP) {
    for (const t of g.transactions) {
      if (t.entries.some((e) => e.kind === 'account' && e.role === role)) {
        hits.push(t);
      }
    }
  }
  return hits;
}

/** Every transaction id that references a given item role. */
export function getTransactionsUsingItem(role: string): PostingTransaction[] {
  const hits: PostingTransaction[] = [];
  for (const g of GL_POSTING_MAP) {
    for (const t of g.transactions) {
      if (t.entries.some((e) => e.kind === 'item' && e.role === role)) {
        hits.push(t);
      }
    }
  }
  return hits;
}

// ============================================================
// 2026-05-30 / JM — Accounting-classification metadata for the
// General Ledger Posting Configuration panel. Hand-curated mapping
// from each QB role (QBAccount or QBItem) to:
//   - the accounting category it belongs to (Balance Sheet, Revenue,
//     Liability, Contra-Revenue)
//   - the display label admins should see (e.g. "Product Revenue"
//     instead of the raw "Product" role name)
//
// Roles not listed here are not rendered on the GL Posting
// Configuration screen — see qb-config.component for the filter.
// ============================================================

export type GlAccountingCategory = 'BalanceSheet' | 'Revenue' | 'ContraRevenue';

export type GlMappingSource = 'QBAccount' | 'QBItem';

export interface GlMappingDescriptor {
  /** Underlying role name as stored in the DB (matches QBAccount.Name or QBItem.Name). */
  role: string;
  /** Which lookup table this role lives in. */
  source: GlMappingSource;
  /** Accounting category for the configuration UI grouping. */
  category: GlAccountingCategory;
  /** User-facing label (e.g. "Product Revenue" for the "Product" role). */
  displayLabel: string;
  /** Optional sub-line explaining what this maps to in accounting terms. */
  sublabel?: string;
}

export const GL_MAPPING_CATEGORIES: Record<GlAccountingCategory, { displayName: string; blurb: string }> = {
  BalanceSheet: {
    displayName: 'QuickBooks Online Balance Sheet Accounts',
    blurb: 'Assets and other balance-sheet positions affected by Uniformax-driven postings.',
  },
  Revenue: {
    displayName: 'QuickBooks Online Invoice Items',
    blurb: 'Income that lands on the income statement when Uniformax invoices a customer.',
  },
  // Liability: {
  //   displayName: 'Liability Accounts',
  //   blurb: 'Amounts owed to third parties (e.g. sales tax collected on behalf of taxing authorities).',
  // },
  ContraRevenue: {
    displayName: 'QuickBooks Online Contra-Revenue Items',
    blurb: 'Reductions to revenue — returns, refunds, and allowances. Reported on the income statement as a deduction from gross sales.',
  },
};

/** The roles that should appear on the GL Posting Configuration screen,
 *  in display order within each category. Roles absent from this list
 *  are hidden from the UI (silent — the DB rows still exist). */
export const GL_MAPPING_DESCRIPTORS: GlMappingDescriptor[] = [
  // Balance Sheet
  {
    role: 'Cash',
    source: 'QBAccount',
    category: 'BalanceSheet',
    displayLabel: 'Cash',
    sublabel: 'Used as the DepositToAccountRef on Sales Receipts, Payments, and Refund Receipts.',
  },
  // Revenue
  {
    role: 'Product',
    source: 'QBItem',
    category: 'Revenue',
    displayLabel: 'Product Revenue',
  },
  {
    role: 'Shipping',
    source: 'QBItem',
    category: 'Revenue',
    displayLabel: 'Shipping Revenue',
  },
  {
    role: 'Handling',
    source: 'QBItem',
    category: 'Revenue',
    displayLabel: 'Handling Revenue',
  },
  {
    role: 'Tax',
    source: 'QBItem',
    category: 'Revenue',
    displayLabel: 'Sales Tax',
    sublabel: 'Sales tax collected on behalf of the taxing authority. Owed until remitted.',
  },
  {
    role: 'RestockingFee',
    source: 'QBItem',
    category: 'Revenue',
    displayLabel: 'Restock Revenue',
    sublabel: 'Restock charge billed as a positive revenue line on refund documents (CreditMemo / RefundReceipt). Replaces the legacy per-account batch invoice — restock is netted into each refund.',
  },
  // Contra-Revenue
  {
    role: 'Returns',
    source: 'QBItem',
    category: 'ContraRevenue',
    displayLabel: 'Returns & Allowances',
    sublabel: 'Reduces gross sales when refunds or credit memos are issued.',
  },
];

/** Returns the descriptors for a given category, in their declared order. */
export function getDescriptorsForCategory(category: GlAccountingCategory): GlMappingDescriptor[] {
  return GL_MAPPING_DESCRIPTORS.filter((d) => d.category === category);
}

/** The visible-row predicate. Rows for roles not in GL_MAPPING_DESCRIPTORS
 *  are silently hidden from the GL Posting Configuration screen. */
export function isVisibleRole(source: GlMappingSource, role: string): boolean {
  return GL_MAPPING_DESCRIPTORS.some((d) => d.source === source && d.role === role);
}
